<template>
  <dashboard-layout title="Overview" kicker="Salon dashboard" :items="navItems">
    <div class="row g-3">
      <div class="col-xl-3 col-lg-6 col-12" v-for="c in statCards" :key="c.id">
        <div class="dash-stat" :class="c.variant">
          <div class="dash-stat-top">
            <div class="dash-stat-label inter-regular-family">{{ c.label }}</div>
            <div class="dash-stat-ic" aria-hidden="true"><i :class="c.icon"></i></div>
          </div>
          <div class="dash-stat-value filson-black-family">{{ c.value }}</div>
          <div class="dash-stat-sub inter-regular-family">{{ c.sub }}</div>
        </div>
      </div>
      <div v-if="statsError" class="col-12">
        <div class="small text-muted">{{ statsError }}</div>
      </div>
    </div>

    <div class="row g-3 mt-1">
      <div class="col-xl-6 col-12" v-for="card in mainCards" :key="card.id">
        <div class="dash-card bg-white">
          <div class="dash-card-head">
            <div class="dash-card-title filson-black-family">{{ card.title }}</div>
            <div class="dash-card-chip inter-regular-family" v-if="card.chip">{{ card.chip }}</div>
          </div>
          <div class="dash-card-body inter-regular-family text-muted">
            {{ card.body }}
          </div>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from './DashboardLayout.vue';
import { SALON_DASH_NAV } from '@/utils/dashboardNav';

export default {
  name: 'SalonDashboard',
  components: { DashboardLayout },
  data() {
    return {
      navItems: SALON_DASH_NAV,
      statsLoading: true,
      statsError: '',
      earningsTotal: 0,
      completedJobCount: 0,
      pendingAssignmentCount: 0,
      inProgressCount: 0,
      assignedListCount: 0,
      // Requirement: only cards in the dashboard body.
      mainCards: [
        { id: 'card-1', title: 'Upcoming Appointments', chip: 'Today', body: 'Quick view of today’s schedule and upcoming bookings.' },
        { id: 'card-2', title: 'Revenue Snapshot', chip: 'This month', body: 'A lightweight overview of earnings and payouts.' },
        { id: 'card-3', title: 'Top Services', chip: 'Insights', body: 'See which services are booked the most this week.' },
        { id: 'card-4', title: 'Customer Activity', chip: 'Insights', body: 'Track new and returning customers at a glance.' },
      ],
    };
  },
  computed: {
    statCards() {
      const loading = this.statsLoading;
      const ph = loading ? '…' : null;
      return [
        {
          id: 'revenue',
          label: 'Earnings',
          value: ph ?? this.fmtMoney(this.earningsTotal),
          sub: 'Total from completed jobs',
          icon: 'fa fa-line-chart',
          variant: 'dash-stat--yellow',
        },
        {
          id: 'completed',
          label: 'Completed jobs',
          value: ph ?? String(this.completedJobCount),
          sub: 'Same total as Earnings page',
          icon: 'fa fa-check',
          variant: 'dash-stat--orange',
        },
        {
          id: 'assigned',
          label: 'Assigned bookings',
          value: ph ?? String(this.assignedListCount),
          sub: 'Shown on Appointments (up to API limit)',
          icon: 'fa fa-calendar-check-o',
          variant: 'dash-stat--teal',
        },
        {
          id: 'pipeline',
          label: 'Pipeline',
          value: ph ?? `${this.pendingAssignmentCount} · ${this.inProgressCount}`,
          sub: 'Awaiting response · In progress',
          icon: 'fa fa-tasks',
          variant: 'dash-stat--blue',
        },
      ];
    },
  },
  mounted() {
    this.loadOverviewStats();
  },
  methods: {
    fmtMoney(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'AED' }).format(n);
      } catch (_) {
        return `${n.toFixed(2)} AED`;
      }
    },
    normalizeAssignmentStatus(b) {
      const s = String(b?.partner_assignment_status || 'pending').toLowerCase().trim();
      if (s === 'accepted' || s === 'declined') return s;
      return 'pending';
    },
    isJobCompleted(b) {
      return String(b?.booking_status || '').toLowerCase().trim() === 'completed';
    },
    async loadOverviewStats() {
      this.statsLoading = true;
      this.statsError = '';
      try {
        const [earRes, listRes] = await Promise.all([
          this.$axios.get('/partner/earnings'),
          this.$axios.get('/partner/assigned-bookings'),
        ]);
        this.earningsTotal = Number(earRes?.data?.total_earnings) || 0;
        this.completedJobCount = Number(earRes?.data?.count) || 0;
        const bookings = Array.isArray(listRes?.data?.bookings) ? listRes.data.bookings : [];
        this.assignedListCount = bookings.length;
        let pending = 0;
        let inProgress = 0;
        bookings.forEach((b) => {
          const pas = this.normalizeAssignmentStatus(b);
          if (pas === 'pending') pending += 1;
          if (pas === 'accepted' && !this.isJobCompleted(b)) inProgress += 1;
        });
        this.pendingAssignmentCount = pending;
        this.inProgressCount = inProgress;
      } catch (_) {
        this.statsError = 'Could not load live stats. Open Appointments / Earnings after signing in.';
        this.earningsTotal = 0;
        this.completedJobCount = 0;
        this.pendingAssignmentCount = 0;
        this.inProgressCount = 0;
        this.assignedListCount = 0;
      } finally {
        this.statsLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.dash-stat {
  border-radius: 14px;
  padding: 14px 14px 12px;
  color: #111;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  min-height: 112px;
}

.dash-stat-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.dash-stat-label {
  font-size: 12px;
  opacity: 0.9;
}

.dash-stat-ic {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.dash-stat-value {
  font-size: 26px;
  line-height: 1.1;
  margin-top: 6px;
}

.dash-stat-sub {
  font-size: 11px;
  opacity: 0.85;
  margin-top: 2px;
}

.dash-stat--orange { background: #ff7a2b; color: #fff; }
.dash-stat--orange .dash-stat-ic { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.18); }
.dash-stat--yellow { background: #ffcc00; }
.dash-stat--teal { background: #02b5ad; color: #fff; }
.dash-stat--teal .dash-stat-ic { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.18); }
.dash-stat--blue { background: #2f6cff; color: #fff; }
.dash-stat--blue .dash-stat-ic { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.18); }

.dash-card {
  border-radius: 14px;
  padding: 16px 16px 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dash-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.dash-card-title {
  font-size: 14px;
  color: #111;
}
.dash-card-chip {
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(248, 114, 31, 0.12);
  color: #f8721f;
  border: 1px solid rgba(248, 114, 31, 0.25);
  white-space: nowrap;
}
.dash-card-body {
  font-size: 12px;
  line-height: 1.5;
}
</style>

